/**
 * theme.ts — Single source of truth for all design colors.
 *
 * Import this file anywhere you need a color:
 *   import { C } from '../theme';
 *   style={{ background: C.bgDeep, color: C.textPrimary, accentColor: C.toolDraw }}
 *
 * To retheme the whole app, change values here only.
 */

// ── Core backgrounds ─────────────────────────────────────────────────────────
const bgDeep    = '#0d0d1a';   // Footer, toolbar, scrollbar track, textarea
const bgBase    = '#12121f';   // Timeline body, About modal
const bgHtml    = '#141414';   // html / body
const bgPanel   = '#1a1a2e';   // UV panel buttons, slight lift
const bgSurface = '#2a2a40';   // Elevated surface (reused as border too — see border below)
const bgFloat   = 'rgba(30,30,50,0.7)';   // Floating panel toggle buttons
const bgPopup   = 'rgba(0,0,0,0.82)';     // Cursor info popup, tooltips

// ── Borders / separators ─────────────────────────────────────────────────────
const border        = '#2a2a40';                  // Main border/separator used everywhere
const borderSubtle  = 'rgba(255,255,255,0.08)';   // Layer row borders (inactive)
const borderFaint   = 'rgba(255,255,255,0.12)';   // UV panel outer border

// ── Scrollbar ────────────────────────────────────────────────────────────────
const scrollTrack      = bgDeep;
const scrollThumb      = '#2e2e48';
const scrollThumbHover = '#3e3e60';

// ── Text hierarchy ───────────────────────────────────────────────────────────
const textPrimary   = '#ccc';   // Active toolbar text, body text
const textSecondary = '#aaa';   // Inactive toolbar text, panel toggles
const textMuted     = '#888';   // Labels, camera buttons
const textDim       = '#555';   // Toolbar hints ("Size", hotkeys in menus)
const textFaint     = '#333';   // Footer, clean autosave dot
const textHeading   = '#fff';   // Primary headings

// ── Color helper ────────────────────────────────────────────────────────────
/** Returns the hex color as rgba() with the given alpha (0–1). */
const withAlpha = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ── Tool accent colors ───────────────────────────────────────────────────────
const toolDraw  = '#58a6ff';   // Draw tool, active UI states, tablet camera
const toolErase = '#f87171';   // Erase tool, out-point
const toolFill  = '#4ade80';   // Fill tool, autosave saving dot, in-point
const toolStamp = '#f5a623';   // Stamp, timeline playhead, dirty dot, locked icon
const toolText  = '#a78bfa';   // Text tool
const toolBlur  = '#22d3ee';   // Blur tool (cyan)

// ── Layer selection ──────────────────────────────────────────────────────────
const selectBorder = 'rgba(24,144,255,0.5)';
const selectBg     = 'rgba(24,144,255,0.08)';

// ── Timeline specials ────────────────────────────────────────────────────────
const inPointColor  = 'rgba(34,197,94,0.45)';
const outPointColor = 'rgba(248,113,113,0.45)';

// ── Tool button active backgrounds (derived from parent tool color) ──────────
const toolDrawSubtle = withAlpha(toolDraw,  0.10);   // draw button unselected bg
const toolDrawBg     = withAlpha(toolDraw,  0.18);
const toolEraseBg    = withAlpha(toolErase, 0.18);
const toolStampBg    = withAlpha(toolStamp, 0.18);
const toolFillBg     = withAlpha(toolFill,  0.18);
const toolTextBg     = withAlpha(toolText,  0.18);
const toolBlurBg     = withAlpha(toolBlur,  0.18);

// Tool button foreground tints (lightened for actionBtn pressed text):
const toolEraseFg  = '#fca5a5';
const toolStampFg  = '#fcd34d';
const toolDrawFg   = '#93c5fd';
const toolFillFg   = '#86efac';
const toolTextFg   = '#c4b5fd';
const toolBlurFg   = '#67e8f9';

// ── Exported token map ───────────────────────────────────────────────────────
export const C = {
  // backgrounds
  bgDeep, bgBase, bgHtml, bgPanel, bgSurface, bgFloat, bgPopup,
  // borders
  border, borderSubtle, borderFaint,
  // scrollbar
  scrollTrack, scrollThumb, scrollThumbHover,
  // text
  textPrimary, textSecondary, textMuted, textDim, textFaint, textHeading,
  // tool accents
  toolDraw, toolErase, toolFill, toolStamp, toolText, toolBlur,
  // selection
  selectBorder, selectBg,
  // timeline
  inPointColor, outPointColor,
  // tool button backgrounds
  toolDrawSubtle, toolDrawBg, toolEraseBg, toolStampBg, toolFillBg, toolTextBg, toolBlurBg,
  // tool button foreground tints
  toolEraseFg, toolStampFg, toolDrawFg, toolFillFg, toolTextFg, toolBlurFg,
} as const;

export type ThemeColor = typeof C[keyof typeof C];
